import type { AppDispatch } from 'store';

import { HTTP_STATUS } from 'models';

import {
  badRequest,
  forbidden,
  notFound,
  postError,
  setGlobalRequestError,
  unauthorized,
  unprocessableEntity
} from './errorSlice';
import { initializeUser } from './userSlice';

let timeout: ReturnType<typeof setTimeout> | null = null;
let sessionTimeoutTriggered = false;

const ignoreHttpError = (payload: any) => payload?.ignoreHttpError?.includes(payload.status);

export const errorMiddleware = (store: any) => (next: any) => (action: any) => {
  const dispatch = store.dispatch as AppDispatch;

  if (!action.payload || ignoreHttpError(action.payload)) {
    return next(action);
  }

  switch (action.payload.status) {
    case HTTP_STATUS.UNAUTHORIZED:
      dispatch(unauthorized());
      break;
    case HTTP_STATUS.UNPROCESSABLE_ENTITY:
      dispatch(unprocessableEntity({ error: action.payload }));
      break;
    case HTTP_STATUS.FORBIDDEN:
      dispatch(forbidden());
      break;
    case HTTP_STATUS.NOT_FOUND:
      dispatch(notFound(true));
      break;
    case HTTP_STATUS.BAD_REQUEST:
      dispatch(badRequest({ error: action.payload }));
      break;
    default:
      if (action.meta?.requestStatus === 'rejected' && action.payload.status) {
        dispatch(setGlobalRequestError({ error: action.payload }));
      }
  }

  // Avoid recursive client logging
  if (action.meta?.requestStatus === 'rejected' && action.type !== 'post/error/rejected') {
    dispatch(postError({ error: action.payload, errorInfo: null }));
  }

  return next(action);
};

export const successMiddleware = (store: any) => (next: any) => (action: any) => {
  const dispatch = store.dispatch as AppDispatch;
  const SESSION_TIMEOUT: number = store.getState().configuration.data?.sessionTimeout || 1000 * 60 * 60 * 9 + 5000;
  if (action?.meta?.requestStatus === 'fulfilled') {
    if (!sessionTimeoutTriggered) {
      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(async () => {
        await dispatch(initializeUser());
        sessionTimeoutTriggered = true;
      }, SESSION_TIMEOUT);
    }
  }

  return next(action);
};
